/**
 * Order History Page
 * Display user's past orders in a table
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Main } from '../../../components/styled/Layout';
import { Card, CardContent } from '../../../components/styled/Card';
import { PrimaryButton } from '../../../components/styled/Button';
import orderService from '../services/orderService';
import useShopFeatures from '../hooks/useShopFeatures';
import './Shop.css';

const OrderHistory = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const features = useShopFeatures();

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await orderService.getOrders();
      setOrders(data || []);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'processing': { label: 'Processing', className: 'status-processing' },
      'shipped': { label: 'Shipped', className: 'status-shipped' },
      'delivered': { label: 'Delivered', className: 'status-delivered' },
      'cancelled': { label: 'Cancelled', className: 'status-cancelled' }
    };
    return statusMap[status] || { label: status, className: 'status-default' };
  };

  if (loading) {
    return (
      <Container>
        <Main>
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading orders...</p>
          </div>
        </Main>
      </Container>
    );
  }

  if (orders.length === 0) {
    return (
      <Container>
        <Main>
          <div className="empty-state">
            <h2>No orders yet</h2>
            <p>You haven't placed any orders yet.</p>
            <PrimaryButton onClick={() => navigate('/shop/catalog')}>
              Start Shopping
            </PrimaryButton>
          </div>
        </Main>
      </Container>
    );
  }

  return (
    <Container>
      <Main>
        <div className="shop-page">
          <h1>Order History</h1>
          
          <div className="orders-list">
            {orders.map(order => {
              const status = getStatusBadge(order.status);
              const items = typeof order.items === 'string' ? JSON.parse(order.items) : (order.items || []);
              
              return (
                <Card 
                  key={order.id} 
                  style={{ marginBottom: '16px', cursor: 'pointer' }}
                  onClick={() => navigate(`/shop/order/${order.id}`)}
                >
                  <CardContent>
                    <div className="order-header">
                      <div>
                        <h3>Order {order.displayOrderId || order.id.substring(0, 8)}</h3>
                        <p className="order-date">
                          {new Date(order.createdAt).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </p>
                      </div>
                      <div>
                        <span className={`order-status-badge ${status.className}`}>
                          {status.label}
                        </span>
                      </div>
                    </div>

                    <div className="order-items-preview">
                      <p>{items.length} item{items.length !== 1 ? 's' : ''}</p>
                      <p className="order-total">Total: ${order.total?.toFixed(2) || '0.00'}</p>
                    </div>

                    {features.invoices && (
                      <PrimaryButton 
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          orderService.downloadInvoice(order.id);
                        }}
                        style={{ marginTop: '12px' }}
                      >
                        Download Invoice
                      </PrimaryButton>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </Main>
    </Container>
  );
};

export default OrderHistory;

